// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * The ReUseX logo's X, as geometry: two strokes in a unit box (u right, v
 * down), stroke A from top-left to bottom-right (drawn solid at rest), stroke
 * B from top-right to bottom-left (the point cloud). Its proportions are
 * measured off the brand face (Barlow Semi Condensed 600) by the component;
 * `DEFAULT_GEOMETRY` stands in until the font has loaded. DOM-free.
 */

export interface XGeometry {
  /** Cap height as a share of the font size. */
  capEm: number;
  /** The X's ink width over its cap height. */
  aspect: number;
  /** One stroke's horizontal width, as a share of the X's width. */
  ws: number;
}

export const DEFAULT_GEOMETRY: XGeometry = { capEm: 0.7, aspect: 0.62, ws: 0.3 };

/** The letters drawn solid before the X in the `word` variant. */
export const WORD_TEXT = 'ReUse';

/** A box in CSS pixels. */
export interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

export const clamp = (x: number, a: number, b: number): number => Math.max(a, Math.min(b, x));

/** Inside stroke A (top-left → bottom-right), widened by `e` on both sides. */
export function inStrokeA(u: number, v: number, g: XGeometry, e = 0): boolean {
  const x0 = v * (1 - g.ws);
  return v >= 0 && v <= 1 && u >= x0 - e && u <= x0 + g.ws + e;
}

/** Inside stroke B (top-right → bottom-left), widened by `e` on both sides. */
export function inStrokeB(u: number, v: number, g: XGeometry, e = 0): boolean {
  const x0 = (1 - v) * (1 - g.ws);
  return v >= 0 && v <= 1 && u >= x0 - e && u <= x0 + g.ws + e;
}

/** 0 outside, 1 in A, 2 in B, 3 where they cross. */
export function strokeMask(u: number, v: number, g: XGeometry): number {
  return (inStrokeA(u, v, g) ? 1 : 0) | (inStrokeB(u, v, g) ? 2 : 0);
}

/** Stroke A's outline in `box`: four corners, clockwise from the top left. */
export function strokeAPolygon(box: Box, g: XGeometry): [number, number][] {
  return [
    [box.x, box.y],
    [box.x + g.ws * box.w, box.y],
    [box.x + box.w, box.y + box.h],
    [box.x + (1 - g.ws) * box.w, box.y + box.h],
  ];
}

export interface Density {
  /** The X's width in px. */
  w: number;
  /** Mean point spacing in px. */
  d: number;
  /** Points per stroke. */
  n: number;
  /** Dot radius in px. */
  r: number;
}

/** How densely an X of cap height `h` px is scanned. */
export function density(h: number, g: XGeometry): Density {
  const w = h * g.aspect;
  const m = clamp(h / 11, 2.4, 14);
  const d = (g.ws * w) / m;
  return { w, d, n: Math.round((2 * g.ws * 0.92 * w * h) / (d * d)), r: Math.max(d * 0.34, 0.35) };
}

/**
 * Geometry from the brand face's metrics for "X" at font size `F`: its ascent
 * and ink width, and the width of one stroke near the top (`stemWidth`, null
 * when it could not be read). Null when the metrics are unusable.
 */
export function geometryFromMetrics(m: {
  F: number;
  ascent: number;
  width: number;
  stemWidth: number | null;
}): XGeometry | null {
  if (!(m.F > 0 && m.ascent > 0 && m.width > 0)) return null;
  const aspect = clamp(m.width / m.ascent, 0.45, 1.2);
  const ws = m.stemWidth !== null && m.stemWidth > 0 ? clamp(m.stemWidth / m.width, 0.18, 0.45) : DEFAULT_GEOMETRY.ws;
  return { capEm: m.ascent / m.F, aspect, ws };
}

export interface WordLayout {
  /** Font size in px. */
  F: number;
  /** Cap height in px (the X's height). */
  cap: number;
  /** Space above and below the ink, for the points' arc. */
  pad: number;
  /** Space left and right of the ink. */
  side: number;
  /** Canvas size in CSS px. */
  W: number;
  H: number;
  /** Text baseline. */
  base: number;
  /** The X's box. */
  X: Box;
  /** Whether `WORD_TEXT` is drawn before the X. */
  hasText: boolean;
}

/**
 * Where everything goes at font size `F`: `textWidth` is `WORD_TEXT`'s
 * advance, `xLeftBearing` the X's left side bearing (`-actualBoundingBoxLeft`,
 * positive when its ink starts right of the pen).
 */
export function wordLayout(o: {
  F: number;
  g: XGeometry;
  textWidth: number;
  xLeftBearing: number;
  hasText: boolean;
}): WordLayout {
  const tw = o.hasText ? o.textWidth : 0;
  const cap = o.g.capEm * o.F;
  const xw = cap * o.g.aspect;
  const pad = Math.ceil(cap * 0.32);
  const gap = o.hasText ? Math.max(0, o.xLeftBearing) : 0;
  const side = pad;
  const X: Box = { x: side + tw + gap, y: pad, w: xw, h: cap };
  return {
    F: o.F,
    cap,
    pad,
    side,
    W: Math.ceil(X.x + xw + side),
    H: Math.ceil(cap + 2 * pad),
    base: pad + cap,
    X,
    hasText: o.hasText,
  };
}
